/** @format */
import angular from 'angular';
import _ from 'lodash';
import { localeServiceModule, ModalService } from 'sis-common';
import { BasicGroupingPanelComponent } from '../grouping-panel';
import { literatureFilterModule } from '../literature/literature.filter';
import { localizedStringEditorModule } from '../string/localizedStringEditor.dir';
import { TabContentSwitchComponent } from '../tabs/tab-content-switch/tab-content-switch.component';
import '../date/filters/localDateRange/localDateRange.filter';
import '../model/officialLanguage.model';
import '../model/gradeScale.model';
import '../courseUnit/substitution.component';
import { commonCodeFilterModule } from '../string/code.filter';
import '../expandables/sisuRegularExpandable/sisuRegularExpandable.component';
import '../prerequisites/prerequisiteGroups.component';
import { PriorLearningSubstitutionsContainerComponent } from '../courseUnit/prior-learning-substitutions/prior-learning-substitutions-container/prior-learning-substitutions-container.component';
import { IconComponent } from '../icon/icon.component';
import { LanguagePillsComponent } from '../language-pills/language-pills.component';
import { OrganisationRoleShareComponent } from '../organisation/organisation-role-share/organisation-role-share.component';
import { commonCourseUnitServiceModule } from '../service/courseUnit.service';
import { commonCurriculumPeriodServiceModule } from '../service/curriculumPeriod.service';
import { commonStudyEventServiceModule } from '../service/studyEvent.service';
import { studyEventSummaryServiceModule } from '../service/studyEventSummary.service';
import { CompletionMethodsComponent } from './completion-methods/completion-methods.component';
import { CourseUnitVersionSelectorComponent } from './course-unit-version-selector/course-unit-version-selector.component';
import { CourseUnitInfoModalComponent } from './courseUnitInfoModal.component';
import courseUnitInfoModalTpl from './courseUnitInfoModal.tpl.html';
import { PlanCourseUnitInfoModalBasicInfoComponent } from './plan-course-unit-info-modal-basic-info/plan-course-unit-info-modal-basic-info.component';

courseUnitInfoModalController.$inject = [
    '$q',
    '$scope',
    '$timeout',
    'defaultPromiseHandler',
    'localeService',
    'officialLanguageModel',
    'gradeScaleJSDataModel',
    'STUDY_TYPE',
    'commonCourseUnitService',
    'commonCurriculumPeriodService',
];
courseUnitInfoModalService.$inject = ['$q', 'modalService', 'commonCourseUnitService'];

export const commonCourseUnitInfoModalModule = 'sis-components.courseUnitInfoModal';

angular
    .module(commonCourseUnitInfoModalModule, [
        literatureFilterModule,
        'pascalprecht.translate',
        localeServiceModule,
        commonCourseUnitServiceModule,
        commonCurriculumPeriodServiceModule,
        commonStudyEventServiceModule,
        studyEventSummaryServiceModule,
        localizedStringEditorModule,
        'sis-components.date.localDateRangeFilter',
        'sis-components.model.courseUnit',
        'sis-components.model.officialLanguage',
        'sis-components.model.gradeScale',
        commonCodeFilterModule,
        CompletionMethodsComponent.downgrade.moduleName,
        PlanCourseUnitInfoModalBasicInfoComponent.downgrade.moduleName,
        PriorLearningSubstitutionsContainerComponent.downgrade.moduleName,
        IconComponent.downgrade.moduleName,
        OrganisationRoleShareComponent.downgrade.moduleName,
        ModalService.downgrade.moduleName,
        BasicGroupingPanelComponent.downgrade.moduleName,
        CourseUnitVersionSelectorComponent.downgrade.moduleName,
        TabContentSwitchComponent.downgrade.moduleName,
        LanguagePillsComponent.downgrade.moduleName,
    ])
    .factory('courseUnitInfoModal', courseUnitInfoModalService)
    .component('courseUnitInfoModal', {
        bindings: {
            close: '&',
            resolve: '<',
        },
        controller: courseUnitInfoModalController,
        controllerAs: 'ctrl',
        template: courseUnitInfoModalTpl,
    });

function courseUnitInfoModalService($q, modalService, commonCourseUnitService) {
    return {
        open: (courseUnit, belongsToCurrentCurriculumPeriod, courseUnitRealisation) =>
            commonCourseUnitService
                .findById(courseUnit.id, { loadRelations: true })
                .then(fetchedCourseUnit =>
                    modalService
                        .open(
                            CourseUnitInfoModalComponent,
                            { belongsToCurrentCurriculumPeriod, courseUnitRealisation, courseUnit: fetchedCourseUnit },
                            { closeWithOutsideClick: true, size: 'xl', windowClass: 'brochure-modal' },
                        )
                        .result.catch(() => $q.reject()),
                ),
    };
}

function courseUnitInfoModalController(
    $q,
    $scope,
    $timeout,
    defaultPromiseHandler,
    localeService,
    officialLanguageModel,
    gradeScaleJSDataModel,
    STUDY_TYPE,
    commonCourseUnitService,
    commonCurriculumPeriodService,
) {
    const ctrl = this;

    ctrl.$onInit = () => {
        ctrl.scope = $scope;
        ctrl.dropdownItems = [];
        ctrl.tabs = [
            { title: 'STUDIES.COURSE_UNIT_INFO_MODAL.CUR_INFO' },
            { title: 'STUDIES.COURSE_UNIT_INFO_MODAL.COMPLETION_METHODS' },
            { title: 'STUDIES.COURSE_UNIT_INFO_MODAL.TITLE_SUBSTITUTIONS' },
        ];
        ctrl.activeTab = 0;

        localeService.getSupportedLocales().then(data => {
            ctrl.locales = data;
            setFallBackName();
        });
        loadOfficialLanguages();
        gradeScaleJSDataModel.lazyGetAll();

        ctrl.courseUnit = ctrl.resolve.courseUnit;
        ctrl.belongsToCurrentCurriculumPeriod = ctrl.resolve.belongsToCurrentCurriculumPeriod;
        ctrl.uiLanguage = localeService.getCurrentLocale();
        ctrl.contentLanguage = ctrl.uiLanguage;
        ctrl.completionMethods = ctrl.courseUnit.completionMethods.filter(cm => cm.studyType !== STUDY_TYPE.OPEN_UNIVERSITY_STUDIES);

        // sort responsibility infos by responsible-teacher > contact-info > administrative-person first and then within each role
        // responsibility infos with personId reference first
        ctrl.sortedByNameResponsibilityInfos = _.chain(ctrl.courseUnit.responsibilityInfos)
            .orderBy([
                info => info.roleUrn === 'urn:code:module-responsibility-info-type:administrative-person',
                info => info.roleUrn === 'urn:code:module-responsibility-info-type:contact-info',
                info => !info.personId,
                info => _.upperCase(_.get(info, 'person.fullName')),
            ])
            .value();

        ctrl.courseUnitVersions = [];
        ctrl.displayNamesByCourseUnitId = {};
        setSelectableVersions().then(setDisplayNames);
    };

    function setFallBackName() {
        _.forEach(ctrl.locales, locale => {
            if (
                !angular.isDefined(ctrl.fallBackName) ||
                (ctrl.fallBackName === '' && angular.isDefined(ctrl.courseUnit.name[locale]) && ctrl.courseUnit.name[locale] !== '')
            ) {
                ctrl.fallBackName = ctrl.courseUnit.name[locale];
            }
        });
    }

    function loadOfficialLanguages() {
        const tempItems = [];
        let langKey;
        ctrl.officialLanguages = officialLanguageModel.getAll();

        _.forEach(ctrl.officialLanguages, lang => {
            langKey = lang.urn.substr(lang.urn.length - 2);
            tempItems[langKey] = {
                key: langKey,
                name: lang.name,
                urn: lang.urn,
            };
        });
        ctrl.dropdownItems = tempItems;
    }

    function setSelectableVersions() {
        return commonCourseUnitService
            .findAllVersionsForStaff(ctrl.courseUnit.groupId, ['ACTIVE', 'DRAFT'])
            .then(allVersions => {
                const curriculumPeriodIds = _.chain(allVersions)
                    .flatMap(version => version.curriculumPeriodIds)
                    .uniq()
                    .value();
                return $q.all([allVersions, commonCurriculumPeriodService.findCurriculumPeriodsByIds(curriculumPeriodIds)]);
            })
            .then(([allVersions, curriculumPeriods]) => {
                const curriculumPeriodsById = _.keyBy(curriculumPeriods, 'id');
                ctrl.courseUnitVersions = allVersions.map(version => ({
                    courseUnit: version,
                    curriculumPeriods: version.curriculumPeriodIds.map(curriculumPeriodId => curriculumPeriodsById[curriculumPeriodId]),
                    usesCourseUnitRealisation: doesVersionUseCourseUnitRealisation(version, ctrl.resolve.courseUnitRealisation),
                }));
            })
            .catch(defaultPromiseHandler.loggingRejectedPromiseHandler);
    }

    function doesVersionUseCourseUnitRealisation(version, courseUnitRealisation) {
        return (
            courseUnitRealisation &&
            version.completionMethods.some(completionMethod =>
                completionMethod.assessmentItemIds.some(assessmentItemId =>
                    courseUnitRealisation.assessmentItemIds.includes(assessmentItemId),
                ),
            )
        );
    }

    function setDisplayNames() {
        return $q.all(ctrl.courseUnitVersions.map(getDisplayName)).then(courseUnitDisplayNames => {
            ctrl.displayNamesByCourseUnitId = _.reduce(
                courseUnitDisplayNames,
                (displayNamesByCourseUnitId, idNamePair) => _.set(displayNamesByCourseUnitId, idNamePair.id, idNamePair.name),
                {},
            );
        });
    }

    function getDisplayName(version) {
        return commonCourseUnitService
            .getDisplayName(version.curriculumPeriods)
            .then(displayName => ({ id: version.courseUnit.id, name: displayName }));
    }

    ctrl.hasCoordinatingOrganisations = () =>
        _.some(ctrl.courseUnit.organisations, { roleUrn: 'urn:code:organisation-role:coordinating-organisation' });

    ctrl.setCourseUnitVersion = courseUnit => {
        $timeout(() => {
            ctrl.courseUnit = courseUnit;
            ctrl.completionMethods = courseUnit.completionMethods.filter(cm => cm.studyType !== STUDY_TYPE.OPEN_UNIVERSITY_STUDIES);
        });
    };
}
